import Radio from '@liveauctioneers/caterwaul-components/lib/Radio/Radio';
import { FormattedMessage, FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { Children, ComponentProps } from 'react';

import { ShippingMethods } from '@/enums/shippingMethod';
import { shippingProviders } from '@/enums/shippingProviders';
import { useAppSelector } from '@/redux/hooks';
import { getShippingQuoteError } from '@/redux/modules/checkoutShippingQuote/checkoutShippingQuote.selectors';
import { ShippingQuote } from '@/types/ShippingQuote';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { ShippingMethodLabel } from '../ShippingMethodLabel';

export type LiveShippingShippingMethodProps = {
    loading?: boolean;
    numberOfItems: number;
    onClick?: ComponentProps<typeof Radio>['onClick'];
    selectedName?: string;
    shippingQuote: ShippingQuote;
};

export const LiveShippingShippingMethod = ({
    loading,
    numberOfItems,
    onClick,
    selectedName,
    shippingQuote,
}: LiveShippingShippingMethodProps) => {
    const formatMessage = useAppFormatMessage();
    const quoteErrorCode = useAppSelector((state) => getShippingQuoteError(state));

    const { currency = 'USD', shippingProvider, total = 0, transitDays } = shippingQuote;

    return (
        <Radio
            className="pl-lg [&.radioCircleClassName]:left-0 [&.radioCircleClassName]:top-3xs [&>span:first-of-type]:w-full"
            disabled={Boolean(quoteErrorCode) || loading}
            label={
                <ShippingMethodLabel
                    info={
                        quoteErrorCode ? (
                            <div className="mt-3xs">
                                <BodySecondary color="critical">
                                    {formatMessage([
                                        'checkout',
                                        'shippingMethod',
                                        quoteErrorCode === 500 ? 'quoteError500' : 'quoteError400',
                                    ])}
                                </BodySecondary>
                            </div>
                        ) : (
                            <span className="block">
                                {Children.toArray(
                                    formatMessage(
                                        [
                                            'checkout',
                                            'shippingMethod',
                                            'method',
                                            transitDays ? 'laShippingDetails' : 'laShippingDetailsNoTransitDays',
                                        ],
                                        {
                                            values: {
                                                days: transitDays || 1,
                                                numberOfItems,
                                                shippingProvider: shippingProviders[shippingProvider],
                                            },
                                        }
                                    )
                                )}
                            </span>
                        )
                    }
                    popoverContent={<FormattedMessage id="checkout.shippingMethod.popovers.laShip" />}
                    price={
                        !quoteErrorCode && (
                            <FormattedNumber
                                currency={currency}
                                key="checkout-shipping-method-live-shipping-price"
                                maximumFractionDigits={2}
                                minimumFractionDigits={2}
                                style="currency"
                                value={Number(total)}
                            />
                        )
                    }
                    title={<FormattedMessage id="checkout.shippingMethod.method.A" />}
                />
            }
            name={ShippingMethods.LiveShipping}
            onClick={onClick}
            selectedName={selectedName}
            testid="shippingMethodLiveShipping"
        />
    );
};
